import React, { useState } from 'react';
import { Tooltip, Button, Pagination, Switch } from 'antd';
import { Edit, Visibility } from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import toast from 'components/Basic/Toast';
import SetupFormModal from 'components/Dashboard/WorldCup/Setup/SetupFormModal';
import { Setup } from 'core/worldCup/setup/types';
import { usePublishSetupMutation, useUnpublishSetupMutation } from 'core/worldCup/setup/SetupService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { PAGE_SIZE_OPTIONS } from 'utilities/constants';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';
dayjs.extend(utc);

type SetupTableType = {
  setupData: Array<Setup | null>;
  total: number;
  current: number;
  pageSize: number;
  onChangePage: (page: number, size: number) => void;
  setCurrent: (e: number) => void;
  setPageSize: (e: number) => void;
  campaignId?: number | null;
};

const SetupTable: React.FC<SetupTableType> = ({
  setupData,
  total,
  current,
  pageSize,
  onChangePage,
  setCurrent,
  setPageSize,
  campaignId,
}) => {
  const [isShowDetailModal, setShowDetailModal] = useState(false);
  const [setupInfo, setSetupInfo] = useState<Setup | null>(null);
  const intl = useIntl();
  const [publishSetup] = usePublishSetupMutation();
  const [unpublishSetup] = useUnpublishSetupMutation();

  const handleTogglePublish = async (checked: boolean, item: Setup | null) => {
    if (!item) return;
    if (!checked && item.hasRaffleConfig) {
      toast.error({ title: intl.formatMessage({ id: 'Cannot unpublish a setup with raffle config' }) });
      return;
    }
    try {
      if (checked) {
        await publishSetup(item.id).unwrap();
      } else {
        await unpublishSetup(item.id).unwrap();
      }
      onChangePage(current, pageSize);
    } catch (e: any) {
      console.error('Error toggling publish:', e);
      const defaultMsg = intl.formatMessage({ id: checked ? 'Error publishing setup' : 'Error unpublishing setup' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const handleChangePage = (page: number, size: number) => {
    setCurrent(page);
    setPageSize(size);
    onChangePage(page, size);
  };

  const handleShowSizeChange = (c: number, s: number) => {
    handleChangePage(1, s);
  };

  const formatDate = (date: string) => {
    return date ? dayjs.utc(date).format('DD/MM/YYYY') : '-';
  };

  return (
    <div className="setup_table_layout" id="setup_table_layout">
      <div className="table_header">
        <div className="id">
          <FormattedMessage id="ID" />
        </div>
        <div className="name">
          <FormattedMessage id="Name" />
        </div>
        <div className="start_at">
          <FormattedMessage id="Start date" />
        </div>
        <div className="end_at">
          <FormattedMessage id="End date" />
        </div>
        <div className="published">
          <FormattedMessage id="Published" />
        </div>
        <div className="actions">
          <FormattedMessage id="Actions" />
        </div>
      </div>
      <div className="table_content" id="setup_table_content">
        {Array.isArray(setupData) &&
          setupData.map((item, index) => (
            <div className={`table_item type_state_${item?.isActive}`} key={index} id={`setup_item_${item?.id}`}>
              <div className="id">
                <p>{item?.id}</p>
              </div>
              <div className="name">
                <p>{item?.name}</p>
              </div>
              <div className="start_at">
                <p>{formatDate(item?.startAt || '')}</p>
              </div>
              <div className="end_at">
                <p>{formatDate(item?.endAt || '')}</p>
              </div>
              <div className="published">
                <Switch
                  checked={item?.isPublished ?? false}
                  disabled={item?.hasRaffleConfig && item?.isPublished}
                  onChange={(checked) => handleTogglePublish(checked, item)}
                />
              </div>
              <div className="actions">
                <Tooltip
                  placement="top"
                  title={intl.formatMessage({ id: (item?.isPublished || item?.hasRaffleConfig) ? 'View setup' : 'Edit setup' })}
                  id="setup_edit_tooltip"
                >
                  <Button
                    className="edit_btn"
                    onClick={() => {
                      setSetupInfo(item);
                      setShowDetailModal(true);
                    }}
                    id="setup_edit_btn"
                  >
                    {(item?.isPublished || item?.hasRaffleConfig) ? <Visibility /> : <Edit />}
                  </Button>
                </Tooltip>
              </div>
            </div>
          ))}
      </div>
      <div className="pagination_wrapper">
        <Pagination
          showSizeChanger
          defaultCurrent={1}
          defaultPageSize={10}
          current={current}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          total={total}
          onChange={handleChangePage}
          onShowSizeChange={handleShowSizeChange}
        />
        <div className="total">Resultados: {total}</div>
      </div>
      <SetupFormModal
        mode="modal"
        isOpenModal={isShowDetailModal}
        onClose={() => setShowDetailModal(false)}
        onSaveSuccess={() => onChangePage(current, pageSize)}
        setup={setupInfo}
        campaignId={campaignId}
      />
    </div>
  );
};

export default SetupTable;
